import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  bookShift,
  type CurrentUser,
  cancelShift,
  type ShiftCalendar,
  type ShiftSlot,
  shiftCalendar,
  startShift,
} from '../api.ts';
import { Announcer } from '../components/Announcer.tsx';
import { InfoTip } from '../components/InfoTip.tsx';
import { apiMessage } from '../messages.ts';

/**
 * "I miei turni".
 *
 * Il mock la disegna come una griglia settimanale. Qui è un **elenco**, e non
 * è una semplificazione: le fasce di un ente sono poche e lunghe — una sera al
 * giorno, non ventiquattro caselle — e un elenco si legge con uno screen
 * reader, si ingrandisce al 200% e sta in un telefono, dove i volontari di
 * 45-65 anni la useranno davvero (sezione 11).
 *
 * Due cose che la griglia dava per contorno e che l'elenco deve restituire, o
 * il cambio di forma è una perdita secca (`docs/DECISIONI.md`, S7):
 *
 *  - **la settimana a colpo d'occhio**: una riga per fascia, con il giorno
 *    dentro la riga. Non una scheda per giorno, che su un telefono fa
 *    quattromila pixel di scorrimento per quattordici sere;
 *  - **l'invito**: la prima fascia scoperta è quella che porta il pulsante
 *    d'accento. È ciò per cui il mock 2 dice che il coordinatore smette di
 *    rincorrere le persone su WhatsApp.
 *
 * L'invito era una **seconda lista** in cima, "Da coprire", con dentro le
 * prime tre fasce scoperte: le stesse righe dell'elenco sotto, con lo stesso
 * pulsante e lo stesso nome accessibile. Chi ascoltava la pagina sentiva due
 * volte "Prendi il turno di giovedì 4 giugno" e non aveva modo di sapere che
 * erano lo stesso turno; chi guardava lo schermo poteva prenotare la stessa
 * sera da due posti. Con quattordici fasce erano ventotto pulsanti più sei
 * doppioni. Adesso l'elenco è uno solo: quante fasce sono scoperte lo dice il
 * conteggio accanto al titolo, e l'invito è l'accento sulla **prima** — una
 * primaria per schermata, come ovunque.
 *
 * Lo stato di ogni fascia è **scritto**, mai solo colorato (sezione 7bis): "In
 * turno: solo tu", "In turno: Paolo", "Nessuno in ascolto" sono i tre stati
 * che il rigo colorato di `.calendar__slot` accompagna, mai sostituisce.
 */

type Props = { user: CurrentUser; headingRef?: React.RefObject<HTMLHeadingElement | null> };

type Outcome = { text: string; ok: boolean };

export function ShiftsPage({ user, headingRef }: Props) {
  const { t, i18n } = useTranslation();

  const [calendar, setCalendar] = useState<ShiftCalendar>();
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [days, setDays] = useState<number>();
  const [busy, setBusy] = useState<string>();
  const [notice, setNotice] = useState<Outcome>();
  /** Su quale fascia è andata storta l'ultima azione: l'esito si dice lì. */
  const [failedSlot, setFailedSlot] = useState<string>();
  /**
   * Il testo da annunciare, con un contatore.
   *
   * Senza il contatore, lo stesso messaggio due volte di fila non cambia il
   * contenuto della regione live e non viene riletto: chi riprova un'azione
   * che fallisce allo stesso modo non sente niente la seconda volta.
   */
  const [announcement, setAnnouncement] = useState({ text: '', seq: 0 });

  const say = useCallback((text: string, ok: boolean) => {
    setNotice({ text, ok });
    setAnnouncement((previous) => ({ text, seq: previous.seq + 1 }));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const range = days
        ? { to: new Date(Date.now() + days * 86_400_000).toISOString() }
        : undefined;
      setCalendar(await shiftCalendar(range));
      setFailed(false);
    } catch {
      // Senza questo, un errore di rete mostrava "non ci sono fasce di
      // apertura": una frase falsa, proprio sotto il messaggio che diceva il
      // vero.
      setFailed(true);
      say(t('shifts.load_failed'), false);
    } finally {
      setLoaded(true);
    }
  }, [days, say, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const timeZone = calendar?.timezone ?? user.tenant?.timezone;
  const locale = i18n.resolvedLanguage;

  /**
   * Il focus resta dove si era.
   *
   * Dopo un'azione la lista si ridisegna e il pulsante premuto sparisce — al
   * suo posto ne compare un altro per la stessa fascia. Senza questo il focus
   * torna su `body` e chi naviga da tastiera riparte dall'inizio del
   * documento, ogni volta (WCAG 2.4.3). È il difetto già corretto una volta
   * sulla console (`docs/DECISIONI.md`, R6).
   */
  const listRef = useRef<HTMLElement>(null);
  const restoreTo = useRef<string>(undefined);

  useEffect(() => {
    // Dipende dal calendario appena arrivato: l'effetto deve girare **dopo**
    // che la lista è stata ridisegnata, altrimenti cercherebbe un pulsante che
    // non c'è ancora.
    const startsAt = calendar && restoreTo.current;
    if (!startsAt) return;
    restoreTo.current = undefined;
    const escaped = startsAt.replace(/["\\]/g, '\\$&');
    listRef.current?.querySelector<HTMLElement>(`[data-slot="${escaped}"] button`)?.focus();
  }, [calendar]);

  const act = async (
    slot: ShiftSlot,
    action: () => Promise<string>,
    fallbackKey: string,
  ): Promise<void> => {
    setBusy(slot.startsAt);
    setNotice(undefined);
    setFailedSlot(undefined);
    try {
      const message = await action();
      restoreTo.current = slot.startsAt;
      say(message, true);
      await refresh();
    } catch (error) {
      setFailedSlot(slot.startsAt);
      say(
        error instanceof ApiError
          ? apiMessage(t, error.messageKey, error.details ?? {})
          : t(fallbackKey),
        false,
      );
    } finally {
      setBusy(undefined);
    }
  };

  const book = (slot: ShiftSlot, range?: { startsAt: string; endsAt: string }): Promise<void> =>
    act(
      slot,
      async () => {
        await bookShift(range ?? { startsAt: slot.startsAt });
        return t('shifts.booked', { when: describeSlot(range ?? slot, locale, timeZone) });
      },
      'shifts.book_failed',
    );

  const giveUp = (slot: ShiftSlot, shiftId: string): Promise<void> =>
    act(
      slot,
      async () => {
        await cancelShift(shiftId);
        return t('shifts.cancelled', { when: describeSlot(slot, locale, timeZone) });
      },
      'shifts.cancel_failed',
    );

  /**
   * Mettersi in ascolto su una fascia già cominciata.
   *
   * Una fascia in corso non si prenota — è già iniziata — ma è anche l'unica
   * in cui l'assenza di qualcuno conta *adesso*: il servizio è aperto, il
   * widget è acceso e non risponde nessuno. Senza questo pulsante la
   * schermata fatta apposta per quel caso non offriva niente. È la stessa
   * azione della console, offerta dove la si scopre.
   */
  const joinNow = (slot: ShiftSlot): Promise<void> =>
    act(
      slot,
      async () => {
        const started = await startShift();
        return t('shifts.joined', { when: formatTime(started.shift.endsAt, locale, timeZone) });
      },
      'shifts.join_failed',
    );

  const slots = calendar?.slots ?? [];
  const gaps = slots.filter((slot) => slot.covered === 0).length;
  /**
   * La fascia scoperta più vicina: è lei a portare l'invito.
   *
   * `covered === 0 && bookable` e non la prima scoperta qualunque: un accento
   * su una fascia che non si può prendere è un invito a premere un pulsante
   * che non c'è.
   */
  const firstGap = slots.find((slot) => slot.covered === 0 && slot.bookable)?.startsAt;

  const rowProps = (slot: ShiftSlot) => ({
    slot,
    myName: user.displayName,
    invited: slot.startsAt === firstGap,
    busy: busy === slot.startsAt,
    error: failedSlot === slot.startsAt ? notice?.text : undefined,
    locale,
    timeZone,
    granularityMinutes: calendar?.bookingGranularityMinutes ?? 30,
    onBook: (range?: { startsAt: string; endsAt: string }) => void book(slot, range),
    onGiveUp: (shiftId: string) => void giveUp(slot, shiftId),
    onJoin: () => void joinNow(slot),
  });

  const horizonDays = calendar
    ? Math.round((new Date(calendar.bookableUntil).getTime() - Date.now()) / 86_400_000)
    : 0;

  return (
    <main className="page" id="main" tabIndex={-1}>
      <h2 ref={headingRef} tabIndex={-1}>
        {t('shifts.title')}
      </h2>

      {/* Sempre montata e vuota: una regione live che nasce piena non parla. */}
      <Announcer polite={announcement.text} assertive="" seq={announcement.seq} />

      {notice && !failedSlot ? (
        <p className={notice.ok ? 'banner' : 'banner banner--danger'}>{notice.text}</p>
      ) : null}

      {failed ? (
        // "Riprova" senza niente da premere è un'istruzione senza il mezzo per
        // eseguirla: il pulsante sta accanto al messaggio.
        <p>
          <button type="button" className="button" onClick={() => void refresh()}>
            {t('shifts.retry')}
          </button>
        </p>
      ) : null}

      {!loaded ? <p className="muted">{t('shifts.loading')}</p> : null}

      {loaded && !failed && slots.length === 0 ? <p className="card">{t('shifts.empty')}</p> : null}

      {slots.length > 0 ? (
        <section className="card" aria-labelledby="calendar-heading" ref={listRef}>
          {/*
            Quante fasce sono scoperte, accanto al titolo.

            Era l'unica cosa che la seconda lista diceva davvero — "guarda qui,
            manca qualcuno" — e per dirla ripeteva tre righe intere. Qui è un
            numero, sta dove si guarda prima di cominciare a scorrere, ed è
            scritto: "3 scoperte", non un rigo rosso.
          */}
          <div className="section-head">
            <div className="section-head__title">
              <h3 id="calendar-heading">{t('shifts.calendar')}</h3>
              <InfoTip>{t('shifts.description')}</InfoTip>
            </div>
            <p className="count">
              {gaps > 0 ? t('shifts.gaps_count', { count: gaps }) : t('shifts.gaps_none')}
            </p>
          </div>
          <ul className="calendar__slots">
            {slots.map((slot) => (
              <SlotRow key={slot.startsAt} {...rowProps(slot)} />
            ))}
          </ul>

          {calendar && new Date(calendar.to) < new Date(calendar.bookableUntil) ? (
            <button
              type="button"
              className="button"
              // Il passo viene dalla finestra che il server ha appena servito,
              // non da un numero riscritto qui: `SHIFT_CALENDAR_DAYS` è
              // configurabile, e un valore duplicato la farebbe accorciare.
              onClick={() =>
                setDays(Math.ceil((new Date(calendar.to).getTime() - Date.now()) / 86_400_000) + 14)
              }
            >
              {t('shifts.week_more')}
            </button>
          ) : (
            <p className="muted">{t('shifts.horizon', { days: horizonDays })}</p>
          )}
        </section>
      ) : null}
    </main>
  );
}

type SlotProps = {
  slot: ShiftSlot;
  /** Il proprio nome pubblico: nell'elenco si dice "tu", non lo si ripete. */
  myName: string;
  /** È la prima fascia scoperta: l'unica che porta l'accento. */
  invited: boolean;
  busy: boolean;
  /** L'esito negativo dell'ultima azione su **questa** fascia. */
  error: string | undefined;
  locale: string | undefined;
  timeZone: string | undefined;
  /** Il passo, in minuti, offerto dal selettore di orario personalizzato. */
  granularityMinutes: number;
  onBook: (range?: { startsAt: string; endsAt: string }) => void;
  onGiveUp: (shiftId: string) => void;
  onJoin: () => void;
};

function SlotRow({
  slot,
  myName,
  invited,
  busy,
  error,
  locale,
  timeZone,
  granularityMinutes,
  onBook,
  onGiveUp,
  onJoin,
}: SlotProps) {
  const { t } = useTranslation();
  const when = describeSlot(slot, locale, timeZone);
  const others = slot.volunteers.filter((name) => name !== myName);

  return (
    <li className={slotClass(slot)} data-slot={slot.startsAt}>
      <div className="calendar__when">
        <span className="calendar__hours">
          {/*
            Una giornata intera si dice "tutto il giorno": un ente aperto
            sempre mostrerebbe altrimenti "dalle 00:00 alle 00:00", che è vero
            e non vuol dire niente.
          */}
          {isWholeDay(slot)
            ? t('shifts.slot_all_day', {
                day: formatDay(new Date(slot.startsAt), locale, timeZone),
              })
            : t('shifts.slot_when', {
                day: formatDay(new Date(slot.startsAt), locale, timeZone),
                from: formatTime(slot.startsAt, locale, timeZone),
                to: formatTime(slot.endsAt, locale, timeZone),
              })}
        </span>
        {/*
          Lo stato della fascia è **scritto**, non solo colorato: chi non
          distingue i colori, e chi ascolta la pagina, legge la stessa cosa.
        */}
        <span className="calendar__who">
          {slot.covered === 0
            ? t('shifts.uncovered')
            : slot.mine && others.length === 0
              ? t('shifts.covered_only_you')
              : slot.mine
                ? t('shifts.covered_with_you', { names: others.join(', ') })
                : t('shifts.covered_by', { names: slot.volunteers.join(', ') })}
        </span>
        {slot.covered > 0 && !slot.fullyCovered ? (
          // Coperta a metà non è coperta: le ore libere sparirebbero proprio
          // dagli occhi di chi guarda il calendario per trovarle.
          <span className="calendar__partial">{t('shifts.covered_partly')}</span>
        ) : null}
        {slot.inProgress ? <span className="calendar__now">{t('shifts.in_progress')}</span> : null}
      </div>

      <div className="calendar__action">
        {slot.inProgress
          ? null
          : slot.myShifts.map((shift) => (
              <button
                key={shift.id}
                type="button"
                className="button"
                disabled={busy}
                aria-label={t('shifts.cancel_named', { when })}
                onClick={() => onGiveUp(shift.id)}
              >
                {t('shifts.cancel')}
              </button>
            ))}

        {slot.bookable ? (
          <button
            type="button"
            /*
              Primario solo dove serve l'invito, cioè **una volta**: la
              condizione era `covered === 0`, e su una settimana in cui quasi
              tutte le fasce sono libere disegnava tredici pulsanti d'accento in
              colonna, che è il modo più veloce di non distinguere più niente.
            */
            className={invited ? 'button button--primary' : 'button'}
            disabled={busy}
            aria-label={t('shifts.book_named', { when })}
            onClick={() => onBook()}
          >
            {t('shifts.book')}
          </button>
        ) : null}

        {slot.bookable ? (
          <CustomTimeBooking
            slot={slot}
            granularityMinutes={granularityMinutes}
            locale={locale}
            timeZone={timeZone}
            busy={busy}
            onBook={onBook}
          />
        ) : null}

        {slot.inProgress && !slot.mine ? (
          <button type="button" className="button button--primary" disabled={busy} onClick={onJoin}>
            {t('shifts.join_now')}
          </button>
        ) : null}

        {slot.inProgress && slot.mine ? (
          <span className="muted">{t('shifts.close_from_console')}</span>
        ) : null}
      </div>

      {error ? (
        // L'esito sta accanto al pulsante premuto: un banner in cima a una
        // pagina lunga, su un telefono, non lo vede nessuno.
        <p className="banner banner--danger calendar__error">{error}</p>
      ) : null}
    </li>
  );
}

/** Una fascia che copre una giornata di calendario, cambio d'ora compreso. */
function isWholeDay(slot: { startsAt: string; endsAt: string }): boolean {
  const hours = (new Date(slot.endsAt).getTime() - new Date(slot.startsAt).getTime()) / 3_600_000;
  return hours >= 23;
}

/**
 * Scegliere un orario diverso dalla fascia intera.
 *
 * "Prenota" da solo copre la fascia da un capo all'altro: è il caso normale,
 * e resta un pulsante — un click, senza altro da guardare. Questo è il caso
 * di chi copre solo una parte della fascia (`docs/DECISIONI.md`, inversione
 * di S2): dietro un `<details>` chiuso di default, per non disegnare due
 * selettori su ogni fascia libera quando quasi nessuno li apre. Gli orari
 * proposti sono a passo fisso — mai minuto per minuto — per la stessa
 * ragione per cui S2 lo escludeva: un turno che comincia a un orario
 * qualunque è un calendario che il coordinatore non riesce più a leggere.
 */
function CustomTimeBooking({
  slot,
  granularityMinutes,
  locale,
  timeZone,
  busy,
  onBook,
}: {
  slot: ShiftSlot;
  granularityMinutes: number;
  locale: string | undefined;
  timeZone: string | undefined;
  busy: boolean;
  onBook: (range: { startsAt: string; endsAt: string }) => void;
}) {
  const { t } = useTranslation();
  const points = gridPoints(slot, granularityMinutes);
  const [startsAt, setStartsAt] = useState(slot.startsAt);
  const [endsAt, setEndsAt] = useState(slot.endsAt);

  // Meno di tre punti sulla griglia vuol dire una sola scelta possibile —
  // la fascia intera, già offerta da "Prenota" — quindi non c'è niente da
  // personalizzare.
  if (points.length < 3) return null;

  const startOptions = points.slice(0, -1);
  const endOptions = points.filter((point) => point > startsAt);

  const changeStart = (value: string) => {
    setStartsAt(value);
    if (endsAt <= value) {
      setEndsAt(points.find((point) => point > value) ?? points[points.length - 1] ?? value);
    }
  };

  // Un id per fascia, non uno fisso: più fasce prenotabili a orario
  // personalizzato stanno montate insieme nella stessa pagina.
  const startId = `custom-start-${slot.startsAt}`;
  const endId = `custom-end-${slot.startsAt}`;

  return (
    <details className="more calendar__custom">
      <summary>{t('shifts.custom_toggle')}</summary>
      <div className="calendar__custom-fields">
        <label htmlFor={startId}>{t('shifts.custom_start_label')}</label>
        <select id={startId} value={startsAt} onChange={(event) => changeStart(event.target.value)}>
          {startOptions.map((point) => (
            <option key={point} value={point}>
              {formatTime(point, locale, timeZone)}
            </option>
          ))}
        </select>
        <label htmlFor={endId}>{t('shifts.custom_end_label')}</label>
        <select id={endId} value={endsAt} onChange={(event) => setEndsAt(event.target.value)}>
          {endOptions.map((point) => (
            <option key={point} value={point}>
              {formatTime(point, locale, timeZone)}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="button"
          disabled={busy}
          aria-label={t('shifts.book_custom_named', {
            from: formatTime(startsAt, locale, timeZone),
            to: formatTime(endsAt, locale, timeZone),
          })}
          onClick={() => onBook({ startsAt, endsAt })}
        >
          {t('shifts.book_custom')}
        </button>
      </div>
    </details>
  );
}

/** Ogni istante prenotabile dentro la fascia, al passo configurato. */
function gridPoints(slot: { startsAt: string; endsAt: string }, stepMinutes: number): string[] {
  const stepMs = stepMinutes * 60_000;
  const start = new Date(slot.startsAt).getTime();
  const end = new Date(slot.endsAt).getTime();
  const points: string[] = [];
  for (let t = start; t <= end; t += stepMs) {
    points.push(new Date(t).toISOString());
  }
  if (points[points.length - 1] !== slot.endsAt) points.push(slot.endsAt);
  return points;
}

function slotClass(slot: ShiftSlot): string {
  if (slot.mine) return 'calendar__slot is-mine';
  if (slot.covered === 0) return 'calendar__slot is-gap';
  return 'calendar__slot is-covered';
}

/**
 * Giorno e ora **dell'ente**, non del browser.
 *
 * Un coordinatore in viaggio non deve vedere i turni spostati di un giorno:
 * gli orari sono in ora locale dell'ente (`docs/DECISIONI.md`, E6).
 */
function formatDay(date: Date, locale: string | undefined, timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric' }).format(date);
  }
}

export function formatTime(
  iso: string,
  locale: string | undefined,
  timeZone: string | undefined,
): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    // Fuso non valido: meglio l'ora universale che una frase senza orari.
    return iso.slice(11, 16);
  }
}

/** Come si nomina una fascia a voce: giorno e ore, mai un identificatore. */
function describeSlot(
  slot: { startsAt: string; endsAt: string },
  locale: string | undefined,
  timeZone: string | undefined,
): string {
  const day = formatDay(new Date(slot.startsAt), locale, timeZone);
  if (isWholeDay(slot)) return day;
  return `${day}, ${formatTime(slot.startsAt, locale, timeZone)}–${formatTime(
    slot.endsAt,
    locale,
    timeZone,
  )}`;
}
